import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '../../services/data.service';
import { ApiService } from '../../services/api.service';
import { CartService } from '../../services/cart.service';
import { toOrderItems } from '../cart.utils';
import { GetOrder, PaymentMethod } from '../../models/cart.model';
import { AppliedPromo, AppliedVoucher, VoucherPayload } from '../../models/voucher.model';
import { PayNowAtTableData, PayNowAtTableResult } from '../../models/pay-at-table.model';
import { VoucherMode } from '../../shared/enums';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-pay-now-at-table',
  templateUrl: './pay-now-at-table.component.html',
  styleUrl: './pay-now-at-table.component.scss',
  standalone: false,
})
export class PayNowAtTableComponent implements OnInit {
  private readonly data = inject<PayNowAtTableData>(MAT_BOTTOM_SHEET_DATA);
  private readonly bottomSheetRef = inject(MatBottomSheetRef<PayNowAtTableComponent>);
  private readonly dataService = inject(DataService);
  private readonly apiService = inject(ApiService);
  private readonly cartService = inject(CartService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  readonly VoucherMode = VoucherMode;
  readonly paymentTypes = this.data.paymentTypes;

  readonly isLoading = signal(false);
  readonly voucher = signal<string | null>(null);
  readonly hideVoucher = signal(false);
  readonly firstOrder = signal<GetOrder | null>(null);
  readonly appliedVoucher = signal<AppliedVoucher | null>(null);
  // An automatic section promotion, separate from appliedVoucher - see CartCheckoutService.
  readonly appliedPromo = signal<AppliedPromo | null>(null);
  readonly tipAmount = signal(0);

  readonly totalWithTip = computed(() => (this.firstOrder()?.total_price ?? 0) + this.tipAmount());
  readonly isOrderFree = computed(() => !!this.firstOrder() && this.totalWithTip() <= 0);

  ngOnInit(): void {
    this.applyVoucher(this.dataService.voucherName());
  }

  onVoucherApplied(data: { voucher: string | null }): void {
    this.applyVoucher(data.voucher);
  }

  onTipApplied(data: { tip_amount: number }): void {
    this.tipAmount.set(data.tip_amount);
  }

  async checkout(): Promise<void> {
    this.isLoading.set(true);
    this.bottomSheetRef.dismiss();
    await this.data.onPayNowAtTable(this.buildResult());
    this.isLoading.set(false);
  }

  async selectPaymentOption(method: PaymentMethod): Promise<void> {
    const result = this.buildResult();
    this.bottomSheetRef.dismiss();
    await this.data.onPayNowAtTable(result, method);
  }

  close(): void {
    this.bottomSheetRef.dismiss();
  }

  private buildResult(): PayNowAtTableResult {
    return {
      isOrderFree: this.isOrderFree(),
      firstOrder: this.firstOrder(),
      appliedVoucher: this.appliedVoucher(),
      voucherName: this.voucher(),
      tipAmount: this.tipAmount(),
    };
  }

  private applyVoucher(voucherName: string | null = null): void {
    this.isLoading.set(true);

    const payload: VoucherPayload = {
      user_id: this.dataService.getUserId(),
      order_items: toOrderItems(this.cartService.cartLines()),
      table_id: this.dataService.table().id,
      voucher_name: voucherName || undefined,
    };

    this.apiService.applyPrepaidCheckout(this.dataService.restaurant().id, payload).subscribe({
      next: res => {
        this.isLoading.set(false);
        const appliedVoucher = Object.keys(res.voucher ?? {}).length > 0 ? res.voucher : null;
        this.voucher.set(voucherName);
        this.hideVoucher.set(appliedVoucher != null);
        this.appliedVoucher.set(appliedVoucher);
        this.appliedPromo.set(res.promo?.amount ? res.promo : null);
        this.firstOrder.set(res.orders.length > 0 ? res.orders[0] : null);
        this.dataService.voucherName.set(appliedVoucher?.mode === VoucherMode.Balance ? voucherName : null);
      },
      error: error => {
        this.isLoading.set(false);
        this.toastService.error(this.translateService.instant(error.error.message));
        this.appliedVoucher.set(null);
        this.appliedPromo.set(null);
        this.hideVoucher.set(false);
        this.dataService.voucherName.set(null);
        if (voucherName) this.applyVoucher(null);
      },
    });
  }
}
